import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Player, BowlerStats } from "../matchTypes";

interface InlinePlayerSelectorProps {
  type: "batsman" | "nonstriker" | "bowler";
  players: Player[];
  onSelect: (playerId: string) => void;
  onClose: () => void;
  currentStrikerId?: string;
  currentNonStrikerId?: string;
  currentBowlerId?: string;
  isIndoor?: boolean;
  reverseBattingActive?: boolean;
  dismissalHistory?: string[];
  replacementMode?: boolean;
  previousPlayerName?: string;
  bowlerStatsMap?: Map<string, BowlerStats>;
  lastBowlerId?: string | null;
  isNextOverSelection?: boolean;
}

const InlinePlayerSelector: React.FC<InlinePlayerSelectorProps> = ({
  type,
  players,
  onSelect,
  onClose,
  currentStrikerId,
  currentNonStrikerId,
  currentBowlerId,
  isIndoor = false,
  reverseBattingActive = false,
  dismissalHistory = [],
  replacementMode = false,
  previousPlayerName,
  bowlerStatsMap,
  lastBowlerId,
  isNextOverSelection = false,
}) => {
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);

  const getPlayerDisplayName = (player: Player): string => {
    let displayName = player.name;
    if (player.isCaptain && player.isKeeper) {
      displayName += " (C) (WK)";
    } else if (player.isCaptain) {
      displayName += " (C)";
    } else if (player.isKeeper) {
      displayName += " (WK)";
    }
    return displayName;
  };

  type EligibleResult = {
    eligible?: Player[];
    retired?: Player[];
    yetToBat?: Player[];
    canBatAgain?: Player[];
    currentlyOut?: Player[];
    reverseDismissed?: Player[];
    notDismissed?: Player[];
  };

  const getEligiblePlayers = (): EligibleResult => {
    if (type === "bowler") {
      return players;
    }

    const excludeIds = new Set([currentStrikerId, currentNonStrikerId].filter(Boolean) as string[]);
    if (type === "nonstriker") excludeIds.delete(currentNonStrikerId as string);

    if (type === "nonstriker") {
      if (isIndoor) {
        const availableBatters = players.filter((p) => p.id !== currentStrikerId);

        if (reverseBattingActive && dismissalHistory.length > 0) {
          const reversedIds = [...dismissalHistory].reverse().filter(id => id !== currentStrikerId);
          const reversedDismissed = reversedIds
            .map(id => availableBatters.find(p => p.id === id))
            .filter((p): p is Player => !!p);
          const notDismissed = availableBatters.filter(
            p => !dismissalHistory.includes(p.id)
          );
          return { reverseDismissed: reversedDismissed, notDismissed };
        }

        const yetToBat = availableBatters.filter((p) => (p.outsCount || 0) === 0 && !p.isOut && !p.isRetiredHurt);
        const canBatAgain = availableBatters.filter((p) => (p.outsCount || 0) > 0 && !p.isOut && !p.isRetiredHurt);
        const retired = availableBatters.filter((p) => !!p.isRetiredHurt);
        const currentlyOut = availableBatters.filter((p) => p.isOut);
        return { yetToBat, canBatAgain, retired, currentlyOut };
      }
    }

    // For batsman (wicket replacement) selection
    if (isIndoor) {
      const availableBatters = players.filter(
        (p) => p.id !== currentStrikerId && p.id !== currentNonStrikerId
      );

      if (reverseBattingActive && dismissalHistory.length > 0) {
        const reversedIds = [...dismissalHistory].reverse().filter(
          id => id !== currentStrikerId && id !== currentNonStrikerId
        );
        const reversedDismissed = reversedIds
          .map(id => availableBatters.find(p => p.id === id))
          .filter((p): p is Player => !!p);
        const notDismissed = availableBatters.filter(
          p => !dismissalHistory.includes(p.id)
        );
        return { reverseDismissed: reversedDismissed, notDismissed };
      }

      const yetToBat = availableBatters.filter(
        (p) => (p.outsCount || 0) === 0 && !p.isOut && !p.isRetiredHurt
      );
      const canBatAgain = availableBatters.filter(
        (p) => (p.outsCount || 0) > 0 && !p.isOut && !p.isRetiredHurt
      );
      const retired = availableBatters.filter((p) => !!p.isRetiredHurt);
      const currentlyOut = availableBatters.filter((p) => p.isOut);

      return { yetToBat, canBatAgain, retired, currentlyOut };
    } else {
      return {
        eligible: players.filter(
          (p) =>
            !p.isOut &&
            !p.isRetiredHurt &&
            p.id !== currentStrikerId &&
            p.id !== currentNonStrikerId
        ),
        retired: players.filter(
          (p) =>
            !!p.isRetiredHurt &&
            p.id !== currentStrikerId &&
            p.id !== currentNonStrikerId
        ),
      };
    }
  };

  const eligiblePlayers = getEligiblePlayers();

  const isPlayerDisabled = (playerId: string): boolean => {
    if (type === "bowler") {
      if (isNextOverSelection && lastBowlerId && playerId === lastBowlerId) return true;
      return false;
    }
    if (type === "batsman") {
      return playerId === currentStrikerId || playerId === currentNonStrikerId;
    }
    if (type === "nonstriker") {
      return playerId === currentStrikerId;
    }
    return false;
  };

  const handlePlayerTap = (playerId: string) => {
    if (isPlayerDisabled(playerId)) return;
    if (replacementMode) {
      setSelectedPlayerId(playerId);
    } else {
      onSelect(playerId);
    }
  };

  const handleConfirm = () => {
    if (selectedPlayerId) {
      onSelect(selectedPlayerId);
    }
  };

  const handleCancel = () => {
    setSelectedPlayerId(null);
    onClose();
  };

  const formatRetiredHurtLabel = (player: Player): string => {
    const parts = [`Retired Hurt • ${player.runs} (${player.balls})`];
    const extras: string[] = [];
    if (player.fours > 0) extras.push(`${player.fours}×4`);
    if (player.sixes > 0) extras.push(`${player.sixes}×6`);
    if (extras.length > 0) parts.push(extras.join(" "));
    return parts.join(" • ");
  };

  const formatBowlerLabel = (player: Player): string => {
    const stats = bowlerStatsMap?.get(player.id);
    if (!stats || stats.balls === 0) return "New bowler • 0 overs";
    const overs = Math.floor(stats.balls / 6) + (stats.balls % 6) / 10;
    return `${overs}-${stats.maidens}-${stats.runsConceded}-${stats.wickets}`;
  };

  const renderPlayerButton = (player: Player, label?: string) => {
    const disabled = isPlayerDisabled(player.id);
    const isSelected = selectedPlayerId === player.id;
    const displayLabel = label || (type === "bowler" ? formatBowlerLabel(player) : undefined);
    return (
      <button
        key={player.id}
        onClick={() => handlePlayerTap(player.id)}
        disabled={disabled}
        className={`w-full border rounded-xl p-4 text-left transition-all group ${
          disabled
            ? "opacity-30 cursor-not-allowed border-slate-700/50 bg-slate-800/30"
            : isSelected
              ? "border-emerald-500 bg-emerald-600/30 ring-2 ring-emerald-400"
              : "border-slate-600/50 bg-slate-700/50 hover:bg-emerald-600/30 hover:border-emerald-500/50"
        }`}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className={`font-bold text-lg transition-colors ${
              disabled ? "text-slate-500" : isSelected ? "text-emerald-300" : "text-white group-hover:text-emerald-300"
            }`}>
              {getPlayerDisplayName(player)}
            </div>
            <div className="text-slate-400 text-sm mt-1">
              {displayLabel || `${player.runs} (${player.balls}) • SR: ${player.balls > 0 ? ((player.runs / player.balls) * 100).toFixed(1) : "0.0"}`}
            </div>
          </div>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
            disabled ? "bg-slate-700/30 text-slate-600"
              : isSelected ? "bg-emerald-600 text-emerald-200"
              : "bg-emerald-600/20 text-emerald-400 group-hover:bg-emerald-600/30"
          }`}>
            {isSelected ? "✓" : "→"}
          </div>
        </div>
      </button>
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center sm:justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleCancel}
      ></div>

      <div className="relative bg-gradient-to-b from-slate-800 to-slate-900 w-full sm:max-w-lg sm:rounded-2xl rounded-t-3xl sm:rounded-b-2xl max-h-[70vh] sm:max-h-[80vh] flex flex-col shadow-2xl border border-white/10 animate-slide-up">
        <div className="flex items-center justify-between p-5 border-b border-white/10 shrink-0">
          <div>
            <h3 className="text-xl font-extrabold text-white">
              {replacementMode
                ? type === "batsman"
                  ? "Replace Striker"
                  : type === "nonstriker"
                    ? "Replace Non-Striker"
                    : "Replace Bowler"
                : type === "batsman"
                  ? "Select New Batsman"
                  : type === "nonstriker"
                    ? "Select Non-Striker"
                    : "Select New Bowler"}
            </h3>
            <p className="text-sm text-slate-400 mt-1">
              {replacementMode && previousPlayerName
                ? `Replacing ${previousPlayerName} — current player stays until confirmed`
                : type === "batsman"
                  ? "Choose a batsman to continue"
                  : type === "nonstriker"
                    ? "Choose the non-striker batsman"
                    : "Choose a bowler for the next over"}
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-700/50 hover:bg-slate-700 text-white transition-colors"
          >
            ×
          </button>
        </div>

        <div
          className="overflow-y-auto flex-1 p-4"
          style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
        >
          {(type === "batsman" || type === "nonstriker") && isIndoor ? (
            <div className="space-y-4">
              {reverseBattingActive && eligiblePlayers.reverseDismissed && eligiblePlayers.reverseDismissed.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-amber-400 font-bold mb-2 px-2">
                    Returning Batters (Reverse Order)
                  </div>
                  <div className="text-xs text-slate-500 mb-2 px-2">Last dismissed bats first</div>
                  <div className="space-y-2">
                    {eligiblePlayers.reverseDismissed.map((player, idx) => (
                      renderPlayerButton(player, `Return #${idx + 1} • Out ×${player.outsCount || 1}`)
                    ))}
                  </div>
                </div>
              )}

              {reverseBattingActive && eligiblePlayers.notDismissed && eligiblePlayers.notDismissed.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-emerald-400 font-bold mb-2 px-2">Yet to Bat</div>
                  <div className="space-y-2">
                    {eligiblePlayers.notDismissed.map((player) => (
                      renderPlayerButton(player, "Fresh • Ready to bat")
                    ))}
                  </div>
                </div>
              )}

              {!reverseBattingActive && eligiblePlayers.yetToBat && eligiblePlayers.yetToBat.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-emerald-400 font-bold mb-2 px-2">Yet to Bat</div>
                  <div className="space-y-2">
                    {eligiblePlayers.yetToBat.map((player) => (
                      renderPlayerButton(player, "Fresh • Ready to bat")
                    ))}
                  </div>
                </div>
              )}

              {!reverseBattingActive && eligiblePlayers.retired && eligiblePlayers.retired.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-orange-400 font-bold mb-2 px-2">Returning Batters</div>
                  <div className="space-y-2">
                    {eligiblePlayers.retired.map((player) => (
                      renderPlayerButton(player, formatRetiredHurtLabel(player))
                    ))}
                  </div>
                </div>
              )}

              {!reverseBattingActive && eligiblePlayers.canBatAgain && eligiblePlayers.canBatAgain.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-blue-400 font-bold mb-2 px-2">Can Bat Again</div>
                  <div className="space-y-2">
                    {eligiblePlayers.canBatAgain.map((player) => (
                      renderPlayerButton(player, `Out ×${player.outsCount} • Available`)
                    ))}
                  </div>
                </div>
              )}

              {!reverseBattingActive && eligiblePlayers.currentlyOut && eligiblePlayers.currentlyOut.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-orange-400 font-bold mb-2 px-2">Currently Out (Can Return)</div>
                  <div className="space-y-2">
                    {eligiblePlayers.currentlyOut.map((player) => (
                      renderPlayerButton(player, `Out ×${player.outsCount || 1} • Can return`)
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {type !== "bowler" && eligiblePlayers.retired && eligiblePlayers.retired.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-orange-400 font-bold mb-2 px-2">Returning Batters</div>
                  <div className="space-y-2">
                    {eligiblePlayers.retired.map((player) => (
                      renderPlayerButton(player, formatRetiredHurtLabel(player))
                    ))}
                  </div>
                </div>
              )}
              {type !== "bowler" && eligiblePlayers.eligible && eligiblePlayers.eligible.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-emerald-400 font-bold mb-2 px-2">Yet to Bat</div>
                  <div className="space-y-2">
                    {eligiblePlayers.eligible.map((player) => (
                      renderPlayerButton(player, "Fresh • Ready to bat")
                    ))}
                  </div>
                </div>
              )}
              {type === "bowler" && (
                <div className="space-y-2">
                  {players.map((player) => (
                    renderPlayerButton(player)
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {replacementMode && selectedPlayerId && (
          <div className="border-t border-white/10 p-4 bg-slate-900/80 backdrop-blur-sm shrink-0">
            <div className="flex items-center gap-3">
              <div className="flex-1 text-sm text-slate-300">
                <span className="text-slate-500">Replace with:</span>{" "}
                <span className="font-bold text-white">
                  {getPlayerDisplayName(players.find(p => p.id === selectedPlayerId)!)}
                </span>
              </div>
              <button
                onClick={handleCancel}
                className="px-4 py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-semibold text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="px-6 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
        @media (min-width: 640px) { .animate-slide-up { animation: none; } }
      `}</style>
    </div>,
    document.body
  );
};

export default InlinePlayerSelector;
